import { initPersonalization } from "./components/desktop/PersonalizationMenu";
import { Desktop } from "./components/desktop/Desktop";
import { CanvasWorkbench } from "./components/canvas/CanvasWorkbench";
import { ChatWorkbench } from "./components/chat/ChatWorkbench";
import { FilesWorkbench } from "./components/files/FilesWorkbench";
import { FileExplorer } from "./components/files/FileExplorer";
import { BrowserWorkbench } from "./components/browser/BrowserWorkbench";
import { GlobalToolsWorkbench } from "./components/tools/GlobalToolsWorkbench";
import { ProjectsWorkbench } from "./components/projects/ProjectsWorkbench";
import { ProjectToolsWorkbench } from "./components/tools/ProjectToolsWorkbench";
import { SettingsWorkbench } from "./components/settings/SettingsWorkbench";
import { TaskBoardWorkbench } from "./components/taskboard/TaskBoardWorkbench";
import { CostLedgerWorkbench } from "./components/cost/CostLedgerWorkbench";
import { MemoryWorkbench } from "./components/memory/MemoryWorkbench";
import { KnowledgeWorkbench } from "./components/knowledge/KnowledgeWorkbench";
import { QuarantineWorkbench } from "./components/quarantine/QuarantineWorkbench";
import { RecipesWorkbench } from "./components/recipes/RecipesWorkbench";
import { ArchMapWorkbench } from "./components/archmap/ArchMapWorkbench";
import { BenchmarkWorkbench } from "./components/benchmark/BenchmarkWorkbench";
import { DriftWorkbench } from "./components/drift/DriftWorkbench";
import { ProvenanceWorkbench } from "./components/provenance/ProvenanceWorkbench";
import { ReplayWorkbench } from "./components/replay/ReplayWorkbench";
import { RegistryWorkbench } from "./components/registry/RegistryWorkbench";
import { AccessControlWorkbench } from "./components/access/AccessControlWorkbench";
import { BlogWorkbench } from "./components/blog/BlogWorkbench";
import { WallpaperWorkbench } from "./components/wallpapers/WallpaperWorkbench";
import { SchedulerWorkbench } from "./components/scheduler/SchedulerWorkbench";
import { AutopilotWorkbench } from "./components/autopilot/AutopilotWorkbench";
import { ArbiterWorkbench } from "./components/arbiter/ArbiterWorkbench";
import { AnalyzerWorkbench } from "./components/analyzer/AnalyzerWorkbench";
import { NotebookWorkbench } from "./components/notebook/NotebookWorkbench";
import { MissionControlWorkbench } from "./components/admin/MissionControlWorkbench";
import { TerminalWorkbench } from "./components/terminal/TerminalWorkbench";
import { InviteAccept } from "./components/auth/InviteAccept";
import { LoginPage } from "./components/auth/LoginPage";
import { LobbyPage } from "./components/auth/LobbyPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WindowManagerProvider } from "./context/WindowManagerContext";
import { apiFetch } from "./lib/api";
import { HomeSurface } from "./routes/HomeSurface";
import { ToolsSurface } from "./routes/ToolsSurface";
import { useEffect } from "react";

function ClientLogBridge() {
  useEffect(() => {
    const emit = () => {
      void apiFetch("/api/logs/client-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: "route_viewed",
          route: window.location.hash.replace("#", "") || "home",
          detail: {
            href: window.location.href,
          },
        }),
      }).catch(() => {});
    };
    emit();
    window.addEventListener("hashchange", emit);
    return () => window.removeEventListener("hashchange", emit);
  }, []);

  return null;
}

function AppRoutes() {
  const { user, authRequired, loading } = useAuth();

  if (loading) {
    return null;
  }

  // Allow invite routes through without auth — invitees don't have accounts yet
  const hashForAuth = window.location.hash.replace('#', '');
  if (authRequired && !user && !hashForAuth.startsWith('invite/') && !hashForAuth.startsWith('magic/')) {
    return <LoginPage />;
  }

  // Magic link verification — exchange token for JWT
  if (!user && hashForAuth.startsWith('magic/')) {
    const magicToken = hashForAuth.replace('magic/', '');
    // Auto-verify on mount
    fetch('/api/auth/magic-link/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: magicToken }),
    }).then(r => r.json()).then(data => {
      if (data.ok && data.token) {
        localStorage.setItem('opentool_token', data.token);
        document.cookie = `ot_session=${data.token}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
        window.location.hash = 'home';
        window.location.reload();
      }
    });
    return <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b949e', fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>Signing in...</div>;
  }

  // Unauthenticated invite route — render InviteAccept directly
  if (!user && hashForAuth.startsWith('invite/')) {
    const inviteToken = hashForAuth.replace('invite/', '');
    return <InviteAccept inviteToken={inviteToken} />;
  }

  // Authenticated but only in "lobby" org (no real org) and not admin → show lobby
  // Allow invite routes through so users can accept invitations
  const hash = window.location.hash.replace('#', '');
  const isInviteRoute = hash.startsWith('invite/');
  const realOrgs = user?.orgs.filter(o => o !== 'lobby') ?? [];
  const isLobbyOnly = user && realOrgs.length === 0;
  if (authRequired && isLobbyOnly && !isInviteRoute) {
    return <LobbyPage />;
  }

  return (
    <Desktop>
      {(route, payload) => {
        const projectName = "workspace";

        // Handle invite routes: #invite/{token}
        if (route.startsWith("invite/")) {
          const inviteToken = route.replace("invite/", "");
          return <InviteAccept inviteToken={inviteToken} />;
        }

        switch (route) {
          case "chat":
            return <ChatWorkbench projectName={projectName} />;

          case "files":
            return <FilesWorkbench projectName={projectName} />;

          case "file-explorer":
            return <FileExplorer />;

          case "browser":
            return <BrowserWorkbench projectName={projectName} />;

          case "analyzer":
            return <AnalyzerWorkbench />;

          case "terminal":
            return <TerminalWorkbench />;

          case "canvas":
            return <CanvasWorkbench payload={payload} />;

          case "projects":
            return <ProjectsWorkbench payload={payload} />;

          case "tools":
            return payload ? (
              <ProjectToolsWorkbench payload={payload} />
            ) : (
              <ToolsSurface projectName={projectName} />
            );

          case "taskboard":
            return <TaskBoardWorkbench />;

          case "cost":
            return <CostLedgerWorkbench />;

          case "memory":
            return <MemoryWorkbench />;

          case "knowledge":
            return <KnowledgeWorkbench />;

          case "quarantine":
            return <QuarantineWorkbench />;

          case "recipes":
            return <RecipesWorkbench />;

          case "archmap":
            return <ArchMapWorkbench />;

          case "benchmark":
            return <BenchmarkWorkbench />;

          case "drift":
            return <DriftWorkbench payload={payload} />;

          case "provenance":
            return <ProvenanceWorkbench payload={payload} />;

          case "replay":
            return <ReplayWorkbench />;

          case "registry":
            return <RegistryWorkbench />;

          case "access":
            return <AccessControlWorkbench />;

          case "scheduler":
            return <SchedulerWorkbench />;

          case "autopilot":
            return <AutopilotWorkbench />;

          case "arbiter":
            return <ArbiterWorkbench />;

          case "notebook":
            return <NotebookWorkbench />;

          case "blog":
            return <BlogWorkbench />;

          case "admin":
            return <MissionControlWorkbench />;

          case "settings":
            return <SettingsWorkbench />;

          case "wallpapers":
            return <WallpaperWorkbench />;

          default:
            return <HomeSurface projectName={projectName} />;
        }
      }}
    </Desktop>
  );
}

// Apply persisted personalization (accent color, bg style, opacity) on load
initPersonalization();

export function App() {
  return (
    <WindowManagerProvider>
      <AuthProvider>
        <ClientLogBridge />
        <AppRoutes />
      </AuthProvider>
    </WindowManagerProvider>
  );
}
